import { useEffect, useRef, useState } from "react";

import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Badge, Heading } from "@stellar/design-system";

import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ErrorWithExtras } from "@/components/ErrorWithExtras";
import { NotificationWithButtons } from "@/components/NotificationWithButtons";
import { SectionHeader } from "@/components/SectionHeader";
import { Stepper } from "@/components/Stepper";
import { Toast } from "@/components/Toast";

import {
  clearDisbursementDraftsErrorAction,
  resetDisbursementDraftsAction,
  saveDisbursementDraftAction,
  submitDisbursementNewDraftAction,
} from "@/store/ducks/disbursementDrafts";

import { getOrgInfoAction } from "@/store/ducks/organization";

import { Routes } from "@/constants/settings";

import { useVerificationTypes } from "@/apiQueries/useVerificationTypes";

import { csvTotalAmount } from "@/helpers/csvTotalAmount";

import { useAllBalances } from "@/hooks/useAllBalances";
import { useRedux } from "@/hooks/useRedux";


import { StepConfirmation } from "./components/StepConfirmation";
import { StepDetails } from "./components/StepDetails";
import { StepInvitation } from "./components/StepInvitation";
import { StepReceivers } from "./components/StepReceivers";
import { StepVerification } from "./components/StepVerification";

import { Disbursement, hasWallet, NewDisbursementStep } from "@/types";

import { AppDispatch } from "@/store";


import "./index.scss";

const STEP_LABELS: string[] = [
  "Details",
  "Invitation preview",
  "Verification",
  "Receivers",
  "Confirmation",
];

const STEP_INDEX: Record<NewDisbursementStep, number> = {
  details: 1,
  invitation: 2,
  verification: 3,
  receivers: 4,
  confirmation: 5,
};

export const DisbursementsNew = () => {
  const { disbursementDrafts, organization } = useRedux("disbursementDrafts", "organization");

  const [draftDetails, setDraftDetails] = useState<Disbursement>();

  const [isDetailsValid, setIsDetailsValid] = useState<boolean>(false);
  const [csvFile, setCsvFile] = useState<File | undefined>();
  const [futureBalance, setFutureBalance] = useState<number>(0);

  const [currentStep, setCurrentStep] = useState<NewDisbursementStep>("details");
  const [isDraftInProgress, setIsDraftInProgress] = useState<boolean>(false);
  const [isSavedDraftMessageVisible, setIsSavedDraftMessageVisible] = useState<boolean>(false);
  const [isResponseSuccess, setIsResponseSuccess] = useState<boolean>(false);
  const notificationRef = useRef<HTMLDivElement | null>(null);

  const isStep1Ready: boolean =
    Boolean(draftDetails?.name?.trim()) &&
    Boolean(draftDetails?.registrationContactType) &&
    Boolean(draftDetails?.asset?.code) &&
    (hasWallet(draftDetails?.registrationContactType) || Boolean(draftDetails?.wallet?.id));

  const isDraftEnabled: boolean = isDetailsValid;

  const { data: verificationTypes } = useVerificationTypes();

  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();

  const apiError =
    (disbursementDrafts.status === "ERROR" && disbursementDrafts.errorString) || false;

  const isSavePending: boolean =
    disbursementDrafts.status === "PENDING" && disbursementDrafts.actionType === "save";

  const handleScrollToTop = (): void => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (!apiError && !isSavedDraftMessageVisible && !isResponseSuccess) return;
    notificationRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [isSavedDraftMessageVisible, apiError, isResponseSuccess]);

  // Ensure organization info is loaded on entry. The Step 1 language dropdown and
  // the email preview rely on `organization.data.supportedLanguages`, which is only
  // populated by getOrgInfoAction. UserSession skips the fetch when org name is
  // already set, so fetch here if the list is not yet populated — but skip the
  // call when data is already in Redux to avoid redundant network requests.
  useEffect(() => {
    if (organization.data.supportedLanguages.length === 0) {
      dispatch(getOrgInfoAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    handleScrollToTop();
    if (disbursementDrafts.newDraftId && disbursementDrafts.status === "SUCCESS") {
      if (disbursementDrafts.actionType === "submit") {
        setCurrentStep("confirmation");
        setIsResponseSuccess(true);
        setIsSavedDraftMessageVisible(false);
      }

      if (disbursementDrafts.actionType === "save") {
        setIsSavedDraftMessageVisible(true);
        setIsDraftInProgress(true);
      }
    }
  }, [disbursementDrafts.actionType, disbursementDrafts.newDraftId, disbursementDrafts.status]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const { allBalances } = useAllBalances();

  const availableBalanceForAsset = allBalances?.find(
    (a) => a.assetCode === draftDetails?.asset?.code,
  )?.balance;

  // Default per-disbursement language to the org-level language once org info loads.
  // User can override via the Language dropdown in Step 1.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    const orgLanguage = organization.data.language;
    if (!orgLanguage) return;
    if (draftDetails?.language) return;
    setDraftDetails(
      (prev) =>
        ({
          ...(prev ?? {}),
          language: orgLanguage,
          wallet: prev?.wallet ?? { id: "", name: "" },
          asset: prev?.asset ?? { id: "", code: "" },
        }) as Disbursement,
    );
  }, [organization.data.language, draftDetails?.language]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const effectiveLanguage: string =
    draftDetails?.language ?? organization.data.language ?? "en";

  const resetState = (): void => {
    setCurrentStep("details");
    setDraftDetails(undefined);
    setIsDetailsValid(false);
    setCsvFile(undefined);
    setIsResponseSuccess(false);
    dispatch(resetDisbursementDraftsAction());
  };

  const handleDiscard = (): void => {
    dispatch(resetDisbursementDraftsAction());
    navigate(Routes.DISBURSEMENTS);
  };

  const handleSaveDraft = (): void => {
    if (currentStep === "details" && !isStep1Ready) return;
    if (currentStep !== "details" && !isDraftEnabled) return;

    setIsSavedDraftMessageVisible(false);
    if (draftDetails) {
      dispatch(
        saveDisbursementDraftAction({
          details: {
            ...draftDetails,
            receiverRegistrationMessageTemplate: "",
          },
          file: csvFile,
        }),
      );
    }
  };

  const handleNext = (): void => {
    handleScrollToTop();
    setIsSavedDraftMessageVisible(false);
    const nextMap: Record<NewDisbursementStep, NewDisbursementStep | null> = {
      details: "invitation",
      invitation: "verification",
      verification: "receivers",
      receivers: "confirmation",
      confirmation: null,
    };
    const next = nextMap[currentStep];
    if (next) setCurrentStep(next);
  };

  const handleBack = (): void => {
    handleScrollToTop();
    dispatch(clearDisbursementDraftsErrorAction());
    const prevMap: Record<NewDisbursementStep, NewDisbursementStep | null> = {
      details: null,
      invitation: "details",
      verification: "invitation",
      receivers: "verification",
      confirmation: "receivers",
    };
    const prev = prevMap[currentStep];
    if (prev) setCurrentStep(prev);
  };

  const handleConfirmAndSend = (): void => {
    handleScrollToTop();
    if (draftDetails && csvFile) {
      dispatch(
        submitDisbursementNewDraftAction({
          details: {
            ...draftDetails,
            receiverRegistrationMessageTemplate: "",
          },
          file: csvFile,
        }),
      );
    }
  };

  const handleCsvFileChange = (file?: File): void => {
    if (apiError) {
      dispatch(clearDisbursementDraftsErrorAction());
    }
    csvTotalAmount({ csvFile: file }).then((totalAmount) => {
      if (!totalAmount) return;

      // Parse row count from the file to display in confirmation
      file?.text().then((text) => {
        const lines = text.split("\n").filter((l) => l.trim().length > 0);
        // Subtract 1 for header row
        const rowCount = Math.max(0, lines.length - 1);

        setDraftDetails({
          ...draftDetails,
          stats: {
            ...draftDetails?.stats,
            totalAmount: totalAmount?.toString() ?? "0",
            paymentsTotalCount: rowCount,
          },
        } as Disbursement);
      });

      const assetBalance =
        allBalances?.find((a) => a.assetCode === draftDetails?.asset?.code)?.balance ?? "0";

      if (totalAmount) {
        setFutureBalance(Number(assetBalance) - totalAmount.toNumber());
      }
    });
    setCsvFile(file);
  };

  const handleViewDetails = (): void => {
    navigate(`${Routes.DISBURSEMENTS}/${disbursementDrafts.newDraftId}`);
    resetState();
  };

  const renderCurrentStep = (): React.ReactElement => {
    switch (currentStep) {
      case "details":
        return (
          <StepDetails
            draftDetails={draftDetails}
            futureBalance={futureBalance}
            isStep1Ready={isStep1Ready}
            isDraftEnabled={isDraftEnabled}
            isSavePending={isSavePending}
            onChange={setDraftDetails}
            onValidate={setIsDetailsValid}
            onNext={handleNext}
            onDiscard={handleDiscard}
            onSaveDraft={handleSaveDraft}
            apiError={apiError}
            onClearError={() => dispatch(clearDisbursementDraftsErrorAction())}
          />
        );
      case "invitation":
        return (
          <StepInvitation
            draftDetails={draftDetails}
            language={effectiveLanguage}
            orgName={organization.data.name}
            smsTemplate={organization.data.receiverRegistrationMessageTemplate ?? ""}
            isDraftEnabled={isDraftEnabled}
            isSavePending={isSavePending}
            onNext={handleNext}
            onBack={handleBack}
            onSaveDraft={handleSaveDraft}
          />
        );
      case "verification":
        return (
          <StepVerification
            draftDetails={draftDetails}
            verificationTypes={verificationTypes ?? []}
            isDraftEnabled={isDraftEnabled}
            isSavePending={isSavePending}
            onVerificationChange={(val: string) =>
              setDraftDetails((prev) => ({ ...prev, verificationField: val } as Disbursement))
            }
            onNext={handleNext}
            onBack={handleBack}
            onSaveDraft={handleSaveDraft}
          />
        );
      case "receivers":
        return (
          <StepReceivers
            draftDetails={draftDetails}
            csvFile={csvFile}
            isDraftEnabled={isDraftEnabled}
            isSavePending={isSavePending}
            availableBalance={availableBalanceForAsset}
            futureBalance={futureBalance}
            onCsvFileChange={handleCsvFileChange}
            onNext={handleNext}
            onBack={handleBack}
            onSaveDraft={handleSaveDraft}
          />
        );
      case "confirmation":
        return (
          <StepConfirmation
            draftDetails={draftDetails}
            csvFile={csvFile}
            futureBalance={futureBalance}
            availableBalance={availableBalanceForAsset}
            language={effectiveLanguage}
            isResponseSuccess={isResponseSuccess}
            isDraftEnabled={isDraftEnabled}
            isSavePending={isSavePending}
            isApprovalRequired={organization.data.isApprovalRequired ?? false}
            disbursementDraftsStatus={disbursementDrafts.status ?? ""}
            notificationRef={notificationRef}
            onConfirmAndSend={handleConfirmAndSend}
            onViewDetails={handleViewDetails}
            onBack={handleBack}
            onSaveDraft={handleSaveDraft}
            onStartNew={resetState}
            onGoToDisbursements={() => {
              dispatch(resetDisbursementDraftsAction());
              navigate(Routes.DISBURSEMENTS);
            }}
            onGoToDrafts={() => {
              dispatch(resetDisbursementDraftsAction());
              navigate(Routes.DISBURSEMENT_DRAFTS);
            }}
          />
        );
    }
  };

  const stepperCurrentStep: number = isResponseSuccess ? 6 : STEP_INDEX[currentStep];

  return (
    <>
      <Breadcrumbs
        steps={[
          { label: "Disbursements", route: Routes.DISBURSEMENTS },
          { label: "New disbursement" },
        ]}
      />

      <SectionHeader>
        <SectionHeader.Row>
          <SectionHeader.Content>
            <Heading as="h2" size="sm">
              New disbursement
            </Heading>
          </SectionHeader.Content>

          <SectionHeader.Content align="right">
            <Toast isVisible={isDraftInProgress} setIsVisible={setIsDraftInProgress}>
              <Badge variant="tertiary">Changes saved</Badge>
            </Toast>
          </SectionHeader.Content>
        </SectionHeader.Row>
      </SectionHeader>

      <Stepper steps={STEP_LABELS} currentStep={stepperCurrentStep} />

      {isSavedDraftMessageVisible ? (
        <NotificationWithButtons
          ref={notificationRef}
          variant="success"
          title="Draft saved"
          buttons={[
            {
              label: "View drafts",
              onClick: () => navigate(Routes.DISBURSEMENT_DRAFTS),
            },
            {
              label: "Dismiss",
              onClick: () => setIsSavedDraftMessageVisible(false),
            },
          ]}
        >
          Your disbursement has been saved.
        </NotificationWithButtons>
      ) : null}

      {apiError ? (
        <NotificationWithButtons
          ref={notificationRef}
          variant="error"
          title={
            disbursementDrafts.actionType === "submit"
              ? "There was an error creating your disbursement"
              : "Error"
          }
        >
          <ErrorWithExtras
            appError={{
              message: apiError,
              extras: disbursementDrafts.errorExtras,
            }}
          />
        </NotificationWithButtons>
      ) : null}

      {renderCurrentStep()}
    </>
  );
};

